import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "recommend-edit" }
const _hoisted_2 = { class: "top-area recommend-edit__top-area" }
const _hoisted_3 = { class: "top-area__content-container" }
const _hoisted_4 = { class: "top-area__checked-info-container" }
const _hoisted_5 = { class: "define-announce-container rounded-base-middle" }
const _hoisted_6 = {
  key: 0,
  class: "define-announce-container__button-area button-area"
}

import { computed, inject, onBeforeUnmount, onMounted, reactive, ref } from 'vue';

import { i18n } from '@/i18n';

import RecommendEditSupplierAnnounce from '@/recommendOrderModule/pages/RecommendEditSupplierAnnounce.vue';
import RecommendEditReceiveAnnounce from '@/recommendOrderModule/pages/RecommendEditReceiveAnnounce.vue';
import RecommendEditDefinedOrder from '@/recommendOrderModule/pages/RecommendEditDefinedOrder.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { simpleCopy } from '@/utils/deepCopy';
import { key } from './useRecommendOrderStore';
import { IRecommendOrderArrivalForRecommendEdit, RecommendEditProgress } from './interfaces/recommendOrder';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { IExtraSimulate } from './interfaces/extraSimulation';
import { ForecastService } from '@/coreDomainsModule/services/forecastService';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IYearMonth } from '@/coreDomainsModule/date';
import { emptyInventory } from '@/coreDomainsModule/entities/inventory';
import { FORECAST_LENGTH_IN_MODAL } from '@/coreDomainsModule/constants';
import { DEFAULT_DISPLAY_FORECAST_MONTH } from '../constants';
import { openConfirmModal } from '@/corePagesModule/components/modal/modalActions';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import TextIconButton from '@/corePagesModule/components/TextIconButton.vue';

/**
 * uiState
 */
interface IRecommendEdit {
  recommendOrderForms: IRecommendOrderArrivalForRecommendEdit[]; // 推奨発注量
  partialDeliveryForms: IArrival[]; // 分納情報
  supplierAnnounce: IAnnounce[]; // 仕入先内示
  checkedIndex: number[];
  useCustomerAnnounce: boolean;
  selectedYearMonth?: IYearMonth;
  monthsRegisterSupplierForecast: number;
  isLastCheckedUserNameEllipsis: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RecommendEdit',
  props: {
    progress: { default: 'EditReceiveAnnounce' },
    recommend: { default: undefined },
    partialDeliveryForms: { default: undefined },
    propExtraSimulate: { default: undefined },
    customerAnnounce: { default: undefined },
    supplierAnnounce: { default: undefined },
    propCheckedIndex: { default: undefined },
    definedOrderScrollPosition: { default: () => ({ x: 0, y: 0 }) }
  },
  emits: ["cancel", "updateBusiness", "saveDefinedOrderScrollPosition"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;

// style variables

//変数
const t = i18n.t;

const store = inject(key);

if (!store) {
  throw new Error('store is undefined');
}

const state: IRecommendEdit = reactive<IRecommendEdit>({
  recommendOrderForms: [],
  partialDeliveryForms: [],
  supplierAnnounce: [],
  checkedIndex: [],
  useCustomerAnnounce: true,
  selectedYearMonth: undefined,
  monthsRegisterSupplierForecast: DEFAULT_DISPLAY_FORECAST_MONTH - 1,
  isLastCheckedUserNameEllipsis: false,
}) as IRecommendEdit;

const definedOrder = ref<any | null>(null);

// イベントフック
onMounted(() => {
  state.recommendOrderForms = simpleCopy(props.recommend ?? []);
  state.partialDeliveryForms = simpleCopy(props.partialDeliveryForms ?? []);
  state.supplierAnnounce = simpleCopy(preSupplierAnnounce);
  state.checkedIndex = simpleCopy(props.propCheckedIndex ?? []);
});

onBeforeUnmount(() => {
  const scrollY = definedOrder.value?.tableRef.scrollTop;
  emits('saveDefinedOrderScrollPosition', 0, scrollY ?? 0);
});

/**
 * モーダルを閉じる
 */
const closeModal = () => {
  emits('cancel', true);
};

// 直近26ヶ月の受領内示を生成する
const getFourMonthCustomerAnnouncesFromArrivals = computed(() => {
  const announces: IAnnounce[] = [];
  ForecastService.setAnnounceYearMonth(announces, new Date(), false, FORECAST_LENGTH_IN_MODAL);
  props.propExtraSimulate?.extraAnnounce?.forEach((it) => {
    const announce = announces.find((announce) => DateService.equalYearMonthInfo(announce, it));
    if (announce) {
      announce.amount = it.amount;
    }
  });
  return announces;
});

// 直近26ヶ月の仕入れ先内示をIRecommendOrderArrivalから生成する
const getFourMonthSupplierAnnouncesFromArrivals = computed(() => {
  const announces: IAnnounce[] = [];
  // arrival.crossDateがundefinedだった時用
  const zeroYearMonthInfo: IYearMonth = {
    year: 0,
    month: 0,
  };

  // 作成年月選択が行われていなかったら、処理を飛ばす
  if (!state.selectedYearMonth) return;

  const selectedDate = DateService.convertIYearMonthToDate(state.selectedYearMonth);
  ForecastService.setAnnounceYearMonth(announces, selectedDate, false, FORECAST_LENGTH_IN_MODAL);

  if (state.useCustomerAnnounce) {
    // 客先内示をそのまま仕入先内示にする
    props.customerAnnounce?.forEach((customerAnnounce) => {
      const announce = announces.find((announce) => DateService.equalYearMonthInfo(announce, customerAnnounce));
      if (announce) {
        announce.amount = customerAnnounce.amount;
      }
    });
  } else {
    // シミュレーションの結果で得られた納期を元に仕入先内示を埋める
    props.recommend?.forEach((arrival) => {
      const announce = announces.find((announce) => DateService.equalYearMonthInfo(announce, arrival.crossDate ?? zeroYearMonthInfo));
      if (announce) {
        announce.amount += arrival.amount;
      }
    });
  }
  // 仕入先内示を更新する
  store.updateSupplierAnnounce(announces);
  return announces;
});

// 前月の仕入先内示情報を埋める
const preSupplierAnnounce: IAnnounce[] = [];
for (let i = 0; i < FORECAST_LENGTH_IN_MODAL; i++) {
  preSupplierAnnounce.push({
    year: 2023,
    month: 11,
    day: 1,
    amount: -1,
    isFocused: false,
  } as IAnnounce);
}

/**
 * 選択された年月情報が作成年月である仕入先内示情報で埋める
 */
const filterAndFillSupplierAnnouncements = computed(() => {
  const announces: IAnnounce[] = [];

  // 年月が選択されていなかったら、飛ばす
  if (!state.selectedYearMonth) return;

  ForecastService.setAnnounceYearMonth(
    announces,
    DateService.convertIYearMonthToDate(state.selectedYearMonth),
    false,
    FORECAST_LENGTH_IN_MODAL
  );

  // YearMonthPicker選択値-2が作成年月の年月情報を作成する
  const updatedYearMonth: IYearMonth = DateService.getShiftedIYearMonthByMonths(
    simpleCopy(state.selectedYearMonth.year),
    simpleCopy(state.selectedYearMonth.month),
    -2
  );

  // createdYearMonthと一致する作成年月の仕入先内示情報を絞り込む
  const filteredSupplierAnnounce = props.supplierAnnounce?.filter(
    (supplierAnnounce) =>
      supplierAnnounce.manageInitiatedYearMonth != null &&
      DateService.equalYearMonthInfo(supplierAnnounce.manageInitiatedYearMonth, updatedYearMonth)
  );

  // filter後の仕入先内示情報で表示用のオブジェクトを埋める
  filteredSupplierAnnounce?.forEach((it) => {
    const announce = announces.find((announce) => DateService.equalYearMonthInfo(announce, it));
    if (announce) {
      announce.amount = it.amount;
    }
  });
  return announces;
});

/**
 * 客先内示確定ボタンを押した時の処理
 */
const onConfirmReceiveAnnounce = () => {
  store.confirmReceiveAnnounce();
};

/**
 * アクティブな進捗状況かどうか
 * @param progress - 進捗状況
 */
const isActive = (progress: RecommendEditProgress) => {
  return !store.state.errorType && props.progress === progress;
};

/**
 * 表示情報を更新する
 */
const updateDisplayBusiness = async () => {
  // 確認モーダルを表示
  const modalResult = await openConfirmModal(
    'question',
    t('表示情報更新(詳細ページ)'),
    t('変更する(詳細ページ)'),
    t('キャンセル'),
    t('表示情報を変更しますか(詳細ページ)')
  );
  if (!modalResult.isConfirmed) return;
  emits('updateBusiness');
};

/**
 * 推奨発注のチェックボックスの状態を変更する
 * @param checkedIndex - チェックボックスのインデックス
 */
const onCheckedOrder = (checkedIndex: number[]) => {
  state.checkedIndex = checkedIndex;
  store.state.checkedIndex = checkedIndex;
};

/**
 * 最終チェック情報のユーザー名が省略されているかをチェックする関数
 * @param e - MouseEvent
 */
const mouseoverLastCheckedUserName = (e: MouseEvent) => {
  const { target } = e;
  if (!(target instanceof HTMLElement)) return;
  state.isLastCheckedUserNameEllipsis = target.offsetWidth < target.scrollWidth;
};

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (isActive('EditCompleted'))
          ? (_openBlock(), _createBlock(TextButton, {
              key: 0,
              class: "top-area__all-initialize-button",
              "button-style": 'btn-secondary',
              size: 'btn-sm',
              onClick: updateDisplayBusiness
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('更新')), 1)
              ], undefined, true),
              _: 1
            }))
          : (_openBlock(), _createElementBlock("button", {
              key: 1,
              class: _normalizeClass(["top-area__all-initialize-button btn-text", !_unref(store)?.state.errorType ? '' : 'top-area__all-initialize-button--inactive']),
              onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(store)?.initializeAll && _unref(store)?.initializeAll(...args)))
            }, _toDisplayString(_unref(t)('すべての値を初期化(詳細ページ)')), 3))
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", null, _toDisplayString(_unref(t)('詳細ページ：最終チェック日時')), 1),
        _createElementVNode("div", null, _toDisplayString(_unref(store).state.formSet.lastCheckedAt
              ? _unref(DateService).convertDateToYYYYMMDDHHMM(_unref(store).state.formSet.lastCheckedAt)
              : _unref(DateService).convertDateToYYYYMMDDHHMMAllHyphen()), 1),
        _withDirectives((_openBlock(), _createElementBlock("div", {
          class: "top-area__checked-info-user-name",
          onMouseover: mouseoverLastCheckedUserName
        }, [
          _createTextVNode(_toDisplayString(_unref(store).state.formSet.lastCheckedUser?.name ?? '----'), 1)
        ], 32)), [
          [
            _directive_tooltip,
            state.isLastCheckedUserNameEllipsis ? _unref(store).state.formSet.lastCheckedUser?.name : undefined,
            void 0,
            { bottom: true }
          ]
        ]),
        _createVNode(TextIconButton, {
          text: _unref(t)('詳細ページ：確認済み'),
          icon: "checkWhite",
          "is-icon-left-side": "",
          class: "top-area__checked-button",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(store).updateLastChecked()))
        }, null, 8, ["text"])
      ])
    ]),
    _createVNode(RecommendEditReceiveAnnounce, {
      "prop-calculated-announce-values": getFourMonthCustomerAnnouncesFromArrivals.value,
      "prop-lead-time": _ctx.propExtraSimulate?.extraLeadTime ?? 0,
      "prop-lead-time-display-value": _unref(store)?.state.selectedLeadTimeDisplayValue,
      "prop-lead-time-unit": _unref(store)?.state.selectedLeadTimeUnit,
      "prop-std-index-infos": _ctx.propExtraSimulate?.extraStdInventory ?? _unref(emptyInventory),
      class: _normalizeClass(["recommend-edit__receive-announce rounded-base-middle", isActive('EditReceiveAnnounce') ? '' : 'recommend-edit__receive-announce--inactive']),
      onConfirmReceiveAnnounce: onConfirmReceiveAnnounce
    }, null, 8, ["prop-calculated-announce-values", "prop-lead-time", "prop-lead-time-display-value", "prop-lead-time-unit", "prop-std-index-infos", "class"]),
    _createVNode(RecommendEditDefinedOrder, {
      ref_key: "definedOrder",
      ref: definedOrder,
      "recommend-order-forms": state.recommendOrderForms,
      "partial-delivery-forms": state.partialDeliveryForms,
      "checked-index": state.checkedIndex,
      "scroll-position": _ctx.definedOrderScrollPosition,
      class: _normalizeClass(["recommend-edit__defined-order rounded-base-middle", isActive('EditRecommend') ? '' : 'recommend-edit__defined-order--inactive']),
      onChangedCheckBoxState: _cache[2] || (_cache[2] = ($event: any) => (onCheckedOrder($event)))
    }, null, 8, ["recommend-order-forms", "partial-delivery-forms", "checked-index", "scroll-position", "class"]),
    _createElementVNode("div", {
      class: _normalizeClass(["recommend-edit__defined-announce", isActive('EditSupplierAnnounce') ? '' : 'recommend-edit__defined-announce--inactive'])
    }, [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(RecommendEditSupplierAnnounce, {
          "calculated-announce-values": getFourMonthSupplierAnnouncesFromArrivals.value ?? [],
          "announce-histories": filterAndFillSupplierAnnouncements.value ?? [],
          "receive-year-month": state.selectedYearMonth,
          "months-register-supplier-forecast": state.monthsRegisterSupplierForecast,
          class: "define-announce-container__supplier-announce",
          onChangeStatus: _cache[3] || (_cache[3] = ($event: any) => (state.useCustomerAnnounce = $event)),
          onChangeYearMonth: _cache[4] || (_cache[4] = ($event: any) => (state.selectedYearMonth = $event)),
          onChangeMonthsRegisterSupplierForecast: _cache[5] || (_cache[5] = ($event: any) => (state.monthsRegisterSupplierForecast = $event))
        }, null, 8, ["calculated-announce-values", "announce-histories", "receive-year-month", "months-register-supplier-forecast"]),
        (_unref(store)?.state.progress === 'EditSupplierAnnounce')
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(TextButton, {
                class: "button-area__revise-button",
                "button-style": 'btn-secondary',
                "tool-tip-value": '',
                "on-click": _unref(store).reviseRecommend
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('もどる')), 1)
                ], undefined, true),
                _: 1
              }, 8, ["on-click"]),
              _createVNode(TextButton, {
                color: "primary",
                size: 'btn-md',
                disabled: !state.selectedYearMonth,
                class: "button-area__define-button",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (_unref(store).confirmSupplierAnnounce(state.monthsRegisterSupplierForecast, state.useCustomerAnnounce, state.selectedYearMonth)))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('確定')), 1)
                ], undefined, true),
                _: 1
              }, 8, ["disabled"])
            ]))
          : _createCommentVNode("", true)
      ])
    ], 2)
  ]))
}
}

})